import { useCallback, useEffect, useState } from 'react'
import { fotosCandidatasApi } from '../api/fotosCandidatas'
import { fmtFechaHora } from '../utils/fecha'
import { colors, text, sx } from '../design/tokens'
import Boton from '../components/Boton'
import ImagenAutenticada from '../components/ImagenAutenticada'
import FichasPropuestas from '../components/FichasPropuestas'
import ContenidoPorEan from '../components/ContenidoPorEan'

/**
 * STOCK → Fotos pendientes (Martin, 18 sep 2026, opción A).
 *
 * Desde el 29 sep 2026 el buscador es POR CÓDIGO DE BARRAS (ContenidoPorEan):
 * el EAN trae foto + ficha, se elige el SKU con el selector de artículos y se
 * aprueba; hay importador para muchos EAN. Lo de abajo (candidatas por SKU y
 * fichas propuestas) queda para lo que ya estaba en la bandeja.
 *
 * Todos los días a las 07:40 el agente busca en la web la foto de los
 * productos con ingreso reciente que no la tienen, la normaliza al canon del
 * catálogo y la deja acá como CANDIDATA con su origen y una verificación
 * visual. Una persona la aprueba (pasa a public/productos/SKU.jpg) o la
 * rechaza. Nada se publica solo.
 */
export default function FotosPendientesPage() {
  const [data, setData] = useState({ pendientes: [], recientes: [], sin_foto: 0 })
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState('')
  const [zoom, setZoom] = useState(null)

  const cargar = useCallback(() => {
    setLoading(true); setErr('')
    fotosCandidatasApi.listar()
      .then(({ data }) => setData(data))
      .catch(() => setErr('No se pudieron cargar las fotos pendientes.'))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const decidir = async (c, accion) => {
    setBusy(c.id); setErr('')
    try {
      if (accion === 'aprobar') await fotosCandidatasApi.aprobar(c.id)
      else {
        const motivo = window.prompt(`¿Por qué se rechaza la foto de ${c.sku}? (opcional)`) ?? ''
        await fotosCandidatasApi.rechazar(c.id, motivo)
      }
      cargar()
    } catch (e) {
      setErr(e?.response?.data?.message || `No se pudo ${accion} la foto de ${c.sku}.`)
    } finally { setBusy(null) }
  }

  // Solo lo accionable: las que el agente no encontró (o fallaron).
  const sinFoto = (data.recientes || []).filter((c) => c.estado === 'no_encontrada' || c.estado === 'error')

  const reintentar = async (sku) => {
    setBusy('buscar'); setErr('')
    try {
      const { data: c } = await fotosCandidatasApi.buscar(sku)
      if (c.estado !== 'pendiente') setErr(`${sku}: ${c.motivo || 'no se encontró la foto.'}`)
      cargar()
    } catch (e) {
      setErr(e?.response?.data?.message || `No se pudo buscar ${sku}.`)
    } finally { setBusy(null) }
  }

  const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)} %`)
  const colorConf = (v) => (v == null ? colors.textMeta : v >= 0.8 ? '#166534' : v >= 0.5 ? '#9a3412' : '#991b1b')

  return (
    <div style={st.page}>
      <div style={st.head}>
        <div>
          <h1 style={st.h1}>Fotos pendientes</h1>
          <p style={st.sub}>
            Buscá por <strong>código de barras</strong>: la foto se busca <strong>gratis</strong> en el catálogo de MercadoLibre y en Open Food Facts y queda adaptada al catálogo.
            {' '}Revisás, elegís el artículo y aprobás: nada se publica solo. Para muchos códigos, importá una planilla.
            {' '}Hoy quedan <strong>{data.sin_foto}</strong> productos con ingreso en 30 días sin foto.
          </p>
        </div>
      </div>

      <ContenidoPorEan />

      {err && <div style={st.err}>{err}</div>}
      {loading && <p style={sx.meta}>Cargando…</p>}

      {!loading && data.pendientes.length > 0 && <h2 style={st.h2}>Fotos encontradas por SKU ({data.pendientes.length})</h2>}

      <div style={st.grid}>
        {data.pendientes.map((c) => (
          <div key={c.id} style={st.card}>
            <button type="button" onClick={() => setZoom(c)} style={st.thumbBtn} title="Ver grande">
              {c.tiene_archivo
                ? <ImagenAutenticada url={c.preview} alt={c.sku} style={st.thumb} title="Ver grande" />
                : <div style={st.sinImg}>sin archivo</div>}
            </button>
            <div style={st.cuerpo}>
              <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>{c.sku}{c.ean ? ` · EAN ${c.ean}` : ''}</div>
              <div style={st.nombre}>{c.detalle}</div>
              <div style={{ fontSize: text.meta, color: colorConf(c.confianza), fontWeight: 600 }}>Confianza {pct(c.confianza)}</div>
              {c.verificacion && (
                <div style={st.verif}>
                  {c.verificacion.coincide === false && <span style={st.alerta}>⚠ el agente duda de que sea el mismo producto</span>}
                  {c.verificacion.fondo_blanco === false && <span style={st.alerta}>⚠ fondo no blanco</span>}
                  {c.verificacion.observacion && <div style={{ color: colors.textMeta }}>{c.verificacion.observacion}</div>}
                </div>
              )}
              {c.motivo && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 4 }}>{c.motivo}</div>}
              <div style={{ fontSize: text.meta, marginTop: 6 }}>
                {c.pagina_url && <a href={c.pagina_url} target="_blank" rel="noreferrer" style={st.link}>Página de origen ↗</a>}
                {c.imagen_url && <a href={c.imagen_url} target="_blank" rel="noreferrer" style={{ ...st.link, marginLeft: 10 }}>Imagen original ↗</a>}
              </div>
              <div style={st.acciones}>
                <Boton onClick={() => decidir(c, 'aprobar')} disabled={busy === c.id || !c.tiene_archivo}>✓ Aprobar y publicar</Boton>
                <Boton variante="ghost" onClick={() => decidir(c, 'rechazar')} disabled={busy === c.id}>✕ Rechazar</Boton>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Las ya decididas no se listan: no hay nada que hacer con ellas (Martin,
          23 sep 2026: "no sé cuál es la utilidad de este listado"). Queda solo
          lo que TIENE una acción pendiente: los productos a los que no se les
          encontró la foto, para reintentar cuando el agente falló por una
          página caída o un sitio que bloquea. */}
      {sinFoto.length > 0 && (
        <>
          <h2 style={st.h2}>No se encontró la foto ({sinFoto.length})</h2>
          <p style={{ ...st.sub, marginTop: 0 }}>Probá de nuevo si el sitio de la bodega estaba caído; si no, hay que subir la foto propia.</p>
          <table style={st.table}>
            <thead><tr><th style={st.th}>SKU</th><th style={st.th}>Producto</th><th style={st.th}>Por qué no se encontró</th><th style={st.th}>Buscada</th><th style={st.th}></th></tr></thead>
            <tbody>
              {sinFoto.map((c) => (
                <tr key={c.id} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                  <td style={{ ...st.td, ...sx.mono }}>{c.sku}</td>
                  <td style={st.td}>{c.detalle}</td>
                  <td style={{ ...st.td, color: colors.textMeta, maxWidth: 520 }}>{c.motivo}</td>
                  <td style={{ ...st.td, color: colors.textMeta, whiteSpace: 'nowrap' }}>{fmtFechaHora(c.buscada_at)}</td>
                  <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                    <Boton variante="secondary" sm disabled={busy === 'buscar'} onClick={() => reintentar(c.sku)}>↻ Buscar de nuevo</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {zoom && (
        <div style={st.overlay} onClick={() => setZoom(null)}>
          <div style={st.zoomBox} onClick={(e) => e.stopPropagation()}>
            <ImagenAutenticada url={zoom.preview} alt={zoom.sku} style={{ width: 600, height: 600, maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', background: '#fff' }} />
            <div style={{ padding: '10px 14px', fontSize: text.body }}><strong>{zoom.sku}</strong> · {zoom.detalle}</div>
          </div>
        </div>
      )}
      <FichasPropuestas />
    </div>
  )
}


const st = {
  page: { padding: '1.5rem 2rem', maxWidth: 1400 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap', marginBottom: 16 },
  h1: { margin: 0, fontSize: text.h1, fontWeight: 600, color: colors.text },
  h2: { margin: '28px 0 10px', fontSize: text.h2, fontWeight: 600, color: colors.text },
  sub: { margin: '6px 0 0', color: colors.textMeta, fontSize: text.body, maxWidth: 760 },
  err: { background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: text.body },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 16 },
  card: { display: 'flex', gap: 14, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, background: colors.surface },
  thumbBtn: { border: `1px solid ${colors.borderSoft}`, background: '#fff', padding: 0, borderRadius: 6, cursor: 'zoom-in', width: 160, height: 160, flexShrink: 0, overflow: 'hidden' },
  thumb: { width: 160, height: 160, objectFit: 'contain', display: 'block' },
  sinImg: { width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMeta, fontSize: text.meta },
  cuerpo: { flex: 1, minWidth: 0 },
  nombre: { fontSize: text.body, fontWeight: 600, color: colors.text, margin: '2px 0 4px' },
  verif: { fontSize: text.meta, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 },
  alerta: { color: '#9a3412', fontWeight: 600 },
  link: { color: colors.link, textDecoration: 'none' },
  acciones: { display: 'flex', gap: 8, marginTop: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { textAlign: 'left', padding: '6px 8px', fontSize: text.meta, color: colors.textMeta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { padding: '6px 8px', verticalAlign: 'top' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: text.meta, fontWeight: 600 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  zoomBox: { background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.35)' },
}
